import { useEffect, useState, useCallback } from "react";
import { ArrowDown, Sparkles } from "lucide-react";

/**
 * NewResultsPill — floating cyan notification that appears at the top of
 * the feed when a fresh generation completes while the user is scrolled
 * UP (away from the newest card). Tapping it smooth-scrolls to the newest
 * card and resets the counter. Auto-hides when the newest card is already
 * in view.
 *
 * Positioned fixed at top-center of the viewport (below the Navbar) with
 * a slide-in animation. Uses Geneo's cyan accent + a subtle gold rim.
 */
export function NewResultsPill({ count, onJump }) {
  if (!count || count < 1) return null;
  return (
    <button
      type="button"
      onClick={onJump}
      data-testid="new-results-pill"
      className="fixed left-1/2 -translate-x-1/2 top-20 z-[60] inline-flex items-center gap-2.5 px-5 py-2.5 rounded-full border border-[#00E5FF]/55 bg-[#0A0A0A]/85 backdrop-blur-2xl text-[#00E5FF] shadow-[0_12px_40px_-8px_rgba(0,229,255,0.5)] hover:bg-[#00E5FF]/15 hover:border-[#00E5FF] transition-all animate-in slide-in-from-top-4 fade-in duration-300 font-mono-hy text-[10px] tracking-[0.26em]"
      title="Jump to the newest result"
    >
      <Sparkles className="w-3.5 h-3.5 text-[#F2C94C]" />
      <span>{count} NEW RESULT{count === 1 ? "" : "S"} READY</span>
      <ArrowDown className="w-3.5 h-3.5" />
    </button>
  );
}

/**
 * useNewResultsSignal — tracks the newest-visible card against the live
 * feed items. When a new terminal item lands AND it's below the viewport
 * (user has scrolled up), we increment the counter. When the user scrolls
 * so the newest card re-enters the viewport, we zero out automatically.
 *
 * Returns `{ count, newestId, jumpToNewest }`. Studio.jsx mounts the pill
 * conditionally on `count > 0`.
 */
export function useNewResultsSignal(items) {
  const [count, setCount] = useState(0);
  const [seenIds, setSeenIds] = useState(() => new Set());
  const [newestId, setNewestId] = useState(null);

  // Determine the newest terminal item from the feed (last done/error entry).
  const latest = [...items].reverse().find(
    (it) => it.status === "done" || it.status === "error",
  );

  // Watch for new terminal items landing. Seed `seenIds` on first render so
  // page refresh doesn't trigger the pill for hydrated results.
  useEffect(() => {
    if (!items.length) return;
    if (seenIds.size === 0) {
      // First tick after mount — mark everything already terminal as seen.
      const seed = new Set(
        items.filter((it) => it.status === "done" || it.status === "error").map((it) => it.id),
      );
      setSeenIds(seed);
      return;
    }
    const freshTerminal = items.filter(
      (it) => (it.status === "done" || it.status === "error") && !seenIds.has(it.id),
    );
    if (!freshTerminal.length) return;
    // Mark seen so we don't double-count re-renders.
    setSeenIds((prev) => {
      const next = new Set(prev);
      freshTerminal.forEach((it) => next.add(it.id));
      return next;
    });
    // Only increment the counter if the newest card is OFF-SCREEN. The
    // auto-scroll effect in Studio.jsx handles the on-screen case.
    const newest = freshTerminal[freshTerminal.length - 1];
    setNewestId(newest.id);
    requestAnimationFrame(() => {
      const el = document.querySelector(`[data-testid="feed-item-${newest.id}"]`);
      if (!el) return;
      const rect = el.getBoundingClientRect();
      const inView = rect.top >= 0 && rect.bottom <= window.innerHeight;
      if (!inView) setCount((c) => c + freshTerminal.length);
    });
  }, [items, seenIds]);

  // Auto-zero when the newest card re-enters the viewport.
  useEffect(() => {
    if (!newestId || count === 0) return;
    const el = document.querySelector(`[data-testid="feed-item-${newestId}"]`);
    if (!el) return;
    const io = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) setCount(0);
      },
      { threshold: 0.5 },
    );
    io.observe(el);
    return () => io.disconnect();
  }, [newestId, count]);

  const jumpToNewest = useCallback(() => {
    if (!newestId) return;
    const el = document.querySelector(`[data-testid="feed-item-${newestId}"]`);
    if (el) el.scrollIntoView({ behavior: "smooth", block: "center" });
    setCount(0);
  }, [newestId]);

  return { count, newestId, jumpToNewest };
}

export default NewResultsPill;
